"use client";

/**
 * MarkdownViewerPanel — responsive wrapper that picks mobile (full-screen
 * dialog) or desktop (right sidebar) based on ``useIsMobile()``.
 *
 * Fetches the file content from the signed ``/files/d/{jwt}`` URL via the
 * ``useMarkdownFile`` hook, manages loading/error states, and handles
 * nested ``.md`` link navigation (clicking a link to another markdown file
 * inside the viewer swaps the URL and re-fetches).
 *
 * The panel is rendered as a sibling of the chat area (desktop) or as an
 * overlay (mobile) by ``ChatPage``.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { X, FileText } from "lucide-react";
import type { Editor } from "@tiptap/core";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { MarkdownOutlineTop } from "@/components/markdown-outline";
import { MarkdownViewerSidebar } from "@/components/markdown-viewer-sidebar";
import { MarkdownViewerToolbar } from "@/components/markdown-viewer-toolbar";
import { MarkdownViewerPinButton } from "@/components/markdown-viewer-pin-button";
import { MarkdownViewerSaveStatus } from "@/components/markdown-viewer-save-status";
import { useIsMobile } from "@/hooks/use-media-query";
import { useMarkdownFile } from "@/hooks/use-markdown-file";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { extractFilePathFromToken } from "@/lib/file-link-rewrite";
import { extractFileToken } from "@/lib/file-links";
import { useMarkdownCrossFileProjection } from "@/lib/markdown-cross-file-links";
import type { AccessTokenGetter } from "@/lib/utils";
import { CollaborativeMarkdownPanel, useCollabCapability } from "@/components/collaborative-markdown-panel";

export interface MarkdownViewerPanelProps {
  sessionId?: string | null;
  /** Signed file-download URL (``/files/d/{jwt}``). */
  url: string;
  /** Filename for display (from the JWT fn claim or link label). */
  fileName: string;
  /** Retrieves a WorkOS token for authenticated PDF downloads. */
  getToken: AccessTokenGetter;
  onClose: () => void;
  /** Signed file URL for the "open in new tab" pop-out. */
  fileUrl?: string;
  /** Called when the user wants to open the viewer in a new tab. */
  onOpenInNewWindow?: () => void;
  /** Pins the decoded file path to the active conversation. */
  onPinFile?: (path: string) => Promise<void> | void;
  /** Whether a decoded file path is already pinned in this UI session. */
  isFilePinned?: (path: string) => boolean;
  /** Decoded path currently being pinned, if any. */
  pinningFilePath?: string | null;
  /** Shared desktop resize controller from the active aside manager. */
  resize: AsidePanelResize;
}

function LegacyMarkdownViewerPanel({
  sessionId,
  url,
  fileName,
  getToken,
  onClose,
  fileUrl,
  onOpenInNewWindow,
  onPinFile,
  isFilePinned,
  pinningFilePath,
  resize,
  readOnly = false,
}: MarkdownViewerPanelProps & { readOnly?: boolean }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // The current URL being viewed. Changes when the user clicks a nested
  // .md link inside the viewer — the hook re-fetches on URL change.
  const [currentUrl, setCurrentUrl] = useState(url);
  const [crossFileNavigation, setCrossFileNavigation] = useState(false);
  const [currentFileName, setCurrentFileName] = useState(fileName);
  const [editor, setEditor] = useState<Editor | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [editable, setEditable] = useState(false);
  const [savedMarkdown, setSavedMarkdown] = useState<string | null>(null);
  const [currentMarkdown, setCurrentMarkdown] = useState<string | null>(null);
  // Keep the latest editor value synchronously available while a signed save
  // is in flight. React state can otherwise still contain the submitted
  // version when the user types another character before that save resolves.
  const currentMarkdownRef = useRef<string | null>(null);
  const activeFilePathRef = useRef<string | null>(null);
  const activeDocumentIdentityRef = useRef<string | null>(null);
  const hasAnimatedRef = useRef(false);
  const currentFilePath = useMemo(() => {
    const token = extractFileToken(currentUrl);
    return token ? extractFilePathFromToken(token) : null;
  }, [currentUrl]);
  const pinned = currentFilePath ? Boolean(isFilePinned?.(currentFilePath)) : false;
  const pinning = currentFilePath != null && pinningFilePath === currentFilePath;
  const dirty =
    savedMarkdown !== null &&
    currentMarkdown !== null &&
    currentMarkdown !== savedMarkdown;
  const {
    content,
    loading,
    error,
    refetch,
    refreshed,
    save,
    saving,
    saveError,
    conflict,
    remoteChange,
    saveOutcomeUnknown,
    checkLatest,
    reloadLatest,
  } = useMarkdownFile(currentUrl, {
    // Policy-authorized exact-file links intentionally hide their path, so
    // the filename is the only stable viewer identity available here.
    documentIdentity: currentFileName,
    // Keep remote staleness refreshes from overwriting an in-progress edit
    // (or changes the user has left unsaved after leaving edit mode).
    pollingPaused: false,
    detectOnly: editable || dirty,
  });
  const renderContent = useMarkdownCrossFileProjection(content, currentUrl, sessionId, getToken);

  // The panel can remain mounted while its query-string URL changes.
  // Synchronise that externally supplied document without affecting nested
  // navigation, which changes only currentUrl below.
  useEffect(() => {
    setCurrentUrl(url);
    setCurrentFileName(fileName);
    setCrossFileNavigation(false);
  }, [url, fileName]);

  // Let the initially opened desktop sidebar slide in. Subsequent document
  // replacements keep their keyed sidebar reset, but happen in place.
  useEffect(() => {
    if (!isMobile) hasAnimatedRef.current = true;
  }, [isMobile]);

  // A new document starts in a clean, read-only state. A reissued signed URL
  // for the same decoded path (or opaque exact-file capability) is only a
  // refreshed capability, so retaining this state keeps the mounted editor
  // and its scroller stable.
  useEffect(() => {
    const previousFilePath = activeFilePathRef.current;
    const sameFileRefresh = previousFilePath !== null || currentFilePath !== null
      ? previousFilePath !== null && previousFilePath === currentFilePath
      : activeDocumentIdentityRef.current !== null
        && activeDocumentIdentityRef.current === currentFileName;
    activeFilePathRef.current = currentFilePath;
    activeDocumentIdentityRef.current = currentFileName;
    if (sameFileRefresh) return;
    setEditable(false);
    setSavedMarkdown(null);
    setCurrentMarkdown(null);
    currentMarkdownRef.current = null;
  }, [currentFileName, currentFilePath, currentUrl]);

  // ── Nested .md link navigation ──────────────────────────────────────────
  // When the user clicks a /files/d/ link to another .md file inside the
  // rendered content, swap the URL and re-fetch. The hook's AbortController
  // cancels the in-flight request if one is still running.
  const handleNavigateFile = useCallback(
    (newUrl: string, newFileName: string) => {
      // Don't discard work by navigating away from an edited document.
      if (editable || dirty || conflict || saveOutcomeUnknown) return;
      setCrossFileNavigation(true);
      setCurrentUrl(newUrl);
      setCurrentFileName(newFileName);
    },
    [conflict, dirty, editable, saveOutcomeUnknown],
  );

  const handleMarkdownLoaded = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setSavedMarkdown(markdown);
    setCurrentMarkdown(markdown);
  }, []);

  const handleMarkdownChange = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setCurrentMarkdown(markdown);
  }, []);

  const handleSave = useCallback(async (markdown?: string): Promise<boolean> => {
    const markdownToSave = markdown ?? currentMarkdown;
    if (markdownToSave === null) return false;
    if (markdownToSave === savedMarkdown) return true;

    const saved = await save(markdownToSave);
    if (saved) {
      setSavedMarkdown(markdownToSave);
      // The PUT may complete after a newer editor update. Keep that newer
      // value dirty instead of falsely marking it as saved.
      const latestMarkdown = currentMarkdownRef.current ?? markdownToSave;
      currentMarkdownRef.current = latestMarkdown;
      setCurrentMarkdown(latestMarkdown);
    }
    return saved;
  }, [currentMarkdown, savedMarkdown, save]);

  const handleToggleEdit = useCallback(() => {
    if (readOnly || conflict || saveOutcomeUnknown) return;
    setEditable((isEditable) => !isEditable);
  }, [readOnly, conflict, saveOutcomeUnknown]);

  const reloadAndDiscard = useCallback(async () => {
    if (!window.confirm(t("viewer.reloadDiscardPrompt"))) return;
    const wasEditable = editable;
    setEditable(false);
    const latest = await reloadLatest();
    if (latest === null) {
      setEditable(wasEditable);
      return;
    }
    currentMarkdownRef.current = latest;
    setSavedMarkdown(latest);
    setCurrentMarkdown(latest);
  }, [editable, reloadLatest, t]);

  const checkSaveOutcome = useCallback(async () => {
    const result = await checkLatest();
    if (result.kind !== "reconciled") return;
    // Preserve a keystroke made after the ambiguous request began. The saved
    // baseline advances, while a newer draft remains dirty for explicit save.
    setSavedMarkdown(result.content);
    if (currentMarkdownRef.current === null) {
      currentMarkdownRef.current = result.content;
      setCurrentMarkdown(result.content);
    }
  }, [checkLatest]);

  // ── Mobile: full-screen dialog overlay ──────────────────────────────────
  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="h-full max-h-full w-full max-w-full rounded-none p-0 gap-0 flex flex-col overflow-hidden" hideCloseButton data-testid="markdown-viewer-dialog">
          {/* Visually-hidden title for accessibility (Radix Dialog requires a title) */}
          <DialogTitle className="sr-only">{currentFileName}</DialogTitle>
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3 shrink-0 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              <MarkdownViewerSaveStatus
                contentLoaded={currentMarkdown != null}
                editable={editable}
                hasSaveHandler={!readOnly && !conflict && !saveOutcomeUnknown}
                dirty={dirty}
                saving={saving}
                saveError={saveError}
              />
              <span
                className="truncate text-sm font-semibold"
                title={currentFileName}
              >
                {currentFileName}
              </span>
              {refreshed && (
                <span
                  className="size-1.5 shrink-0 rounded-full bg-blue-500"
                  title={t("viewer.contentUpdated")}
                  data-testid="markdown-viewer-refreshed-indicator"
                />
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <MarkdownViewerPinButton
                filePath={currentFilePath}
                onPinFile={onPinFile}
                pinned={pinned}
                pinning={pinning}
              />
              <MarkdownViewerToolbar
                editor={editor}
                sourceMarkdown={dirty ? currentMarkdown : content}
                fileName={currentFileName}
                getToken={getToken}
                fileUrl={fileUrl}
                onOpenInNewWindow={onOpenInNewWindow}
                editable={editable}
                dirty={dirty}
                saving={saving}
                saveError={saveError}
                saveBlocked={Boolean(conflict || saveOutcomeUnknown)}
                onToggleEdit={readOnly ? undefined : handleToggleEdit}
                onSave={readOnly || conflict || saveOutcomeUnknown ? undefined : handleSave}
                contentVersion={currentMarkdown}
                compact
              />
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={onClose}
                aria-label={t("viewer.closeViewer")}
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
          {/* Content */}
          <div ref={contentScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden p-4 min-w-0">
            {loading && content == null ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                <Spinner className="size-5" />
                <p className="text-sm">{t("common:status.loadingDocument")}</p>
              </div>
            ) : error && content == null ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-center p-4">
                <p className="text-sm text-destructive">{crossFileNavigation ? t("chat:crossFileLinks.unavailable") : error}</p>
                <Button variant="outline" size="sm" onClick={refetch}>
                  {t("common:actions.retry")}
                </Button>
              </div>
            ) : content != null ? (
              <>
                {readOnly && <p role="status" className="mb-2 rounded bg-muted p-2 text-xs">{t("viewer.collabReadOnly")}</p>}
                {remoteChange && !conflict && !saveOutcomeUnknown && (
                  <p className="mb-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    {t("viewer.remoteChange")}
                  </p>
                )}
                {saveOutcomeUnknown && (
                  <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs" role="alert">
                    <span>{t("viewer.saveOutcomeUnknown")}</span>
                    <Button size="sm" variant="outline" onClick={() => void checkSaveOutcome()}>{t("viewer.checkLatest")}</Button>
                  </div>
                )}
                {conflict && (
                  <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-xs" role="alert">
                    <span>{t("viewer.fileChangedElsewhere")}</span>
                    <Button size="sm" variant="outline" onClick={() => void reloadAndDiscard()}>{t("viewer.reloadAndDiscard")}</Button>
                  </div>
                )}
                <MarkdownOutlineTop
                  editor={editor}
                  scrollContainerRef={contentScrollRef}
                  contentVersion={currentMarkdown ?? content}
                />
                <MarkdownViewer
                  content={content}
                  renderContent={!editable && !dirty ? renderContent : null}
                  fileName={currentFileName}
                  editable={editable}
                  saving={saving}
                  onNavigateFile={handleNavigateFile}
                  onEditorReady={setEditor}
                  onMarkdownLoaded={handleMarkdownLoaded}
                  onMarkdownChange={handleMarkdownChange}
                  preserveScrollOnContentChange={refreshed && !editable}
                  scrollContainerRef={contentScrollRef}
                />
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Desktop: right sidebar ──────────────────────────────────────────────
  return (
    <AnimatePresence>
      <MarkdownViewerSidebar
        key={currentFilePath ?? currentFileName}
        animate={!hasAnimatedRef.current}
        content={content}
        renderContent={!editable && !dirty ? renderContent : null}
        loading={loading}
        error={crossFileNavigation && error ? t("chat:crossFileLinks.unavailable") : error}
        fileName={currentFileName}
        getToken={getToken}
        onClose={onClose}
        onNavigateFile={handleNavigateFile}
        onRetry={refetch}
        refreshed={refreshed}
        editable={editable}
        dirty={dirty}
        saving={saving}
        saveError={saveError}
        remoteChange={remoteChange}
        conflict={Boolean(conflict)}
        saveOutcomeUnknown={saveOutcomeUnknown}
        compatibilityNotice={readOnly}
        compatibilityNoticeKey="viewer.collabReadOnly"
        onReloadAndDiscard={reloadAndDiscard}
        onCheckSaveOutcome={checkSaveOutcome}
        currentMarkdown={currentMarkdown}
        onToggleEdit={readOnly ? undefined : handleToggleEdit}
        onSave={readOnly || conflict || saveOutcomeUnknown ? undefined : handleSave}
        onMarkdownLoaded={handleMarkdownLoaded}
        onMarkdownChange={handleMarkdownChange}
        width={resize.width}
        fileUrl={fileUrl}
        onOpenInNewWindow={onOpenInNewWindow}
        filePath={currentFilePath}
        onPinFile={onPinFile}
        pinned={pinned}
        pinning={pinning}
        onResizePointerDown={resize.onPointerDown}
        onResizePointerMove={resize.onPointerMove}
        onResizePointerUp={resize.onPointerUp}
        onResizeDoubleClick={resize.onDoubleClick}
      />
    </AnimatePresence>
  );
}

export function MarkdownViewerPanel(props: MarkdownViewerPanelProps) {
  const { t } = useTranslation();
  const capability = useCollabCapability(props.url, props.getToken, props.fileName);
  const mobile = useIsMobile();
  if (capability.kind === "available") {
    return <CollaborativeMarkdownPanel {...props} room={capability.room} />;
  }
  if (capability.kind === "loading") {
    return <div className="flex h-full items-center justify-center border-l bg-background"
      style={{ width: mobile ? "100%" : props.resize.width }} role="status">
      <Spinner className="size-5" />
      <Button variant="ghost" size="icon" onClick={props.onClose} aria-label={t("viewer.closeViewer")}>
        <X className="size-4" />
      </Button>
    </div>;
  }
  // A missing endpoint is an expected compatibility state. The legacy editor
  // remains intact, including dirty, conditional PUT, and outcome-unknown UX.
  return <LegacyMarkdownViewerPanel {...props} readOnly={capability.kind === "readOnly"} />;
}
